/** The "Herhalen" part of the template settings. */
import { useTranslation } from 'react-i18next';
import type { TemplateSchedule } from '@nexus/core';
import { weekdayName } from './scheduleLabel';

const field = 'rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';

export function ScheduleFields({
  value,
  onChange,
}: {
  value: TemplateSchedule | null;
  onChange: (next: TemplateSchedule | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const set = (patch: Partial<TemplateSchedule>) =>
    onChange({
      every: 'week',
      weekday: 1,
      monthDay: 1,
      time: '09:00',
      // A new schedule starts now: nothing is made for moments already past.
      since: new Date().toISOString(),
      ...value,
      ...patch,
    });

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1">{t('templates.repeat.title')}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label={t('templates.repeat.every')}
          value={value?.every ?? 'never'}
          onChange={(e) =>
            e.target.value === 'never'
              ? onChange(null)
              : set({ every: e.target.value as TemplateSchedule['every'] })
          }
          className={field}
        >
          <option value="never">{t('templates.repeat.never')}</option>
          <option value="day">{t('templates.repeat.day')}</option>
          <option value="weekdays">{t('templates.repeat.weekdays')}</option>
          <option value="week">{t('templates.repeat.week')}</option>
          <option value="month">{t('templates.repeat.month')}</option>
        </select>
        {value?.every === 'week' && (
          <select
            aria-label={t('templates.repeat.weekday')}
            value={value.weekday}
            onChange={(e) => set({ weekday: Number(e.target.value) })}
            className={field}
          >
            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
              <option key={d} value={d}>
                {weekdayName(d, i18n.language)}
              </option>
            ))}
          </select>
        )}
        {value?.every === 'month' && (
          <label className="flex items-center gap-1">
            {t('templates.repeat.onDay')}
            <input
              type="number"
              min={1}
              max={31}
              aria-label={t('templates.repeat.monthDay')}
              value={value.monthDay}
              onChange={(e) =>
                set({ monthDay: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })
              }
              className={`${field} w-16`}
            />
          </label>
        )}
        {value && (
          <label className="flex items-center gap-1">
            {t('templates.repeat.at')}
            <input
              type="time"
              aria-label={t('templates.repeat.time')}
              value={value.time}
              onChange={(e) => e.target.value && set({ time: e.target.value })}
              className={field}
            />
          </label>
        )}
      </div>
      {value && <p className="text-xs text-fg-muted">{t('templates.repeat.help')}</p>}
    </fieldset>
  );
}
